import { useEffect, useRef } from 'react';
import NewWindowIcon from '@/public/icons/approval/new-window.svg';
import {
  DropdownItem,
  DropdownItemIconWrap,
  DropdownItemLink,
  DropdownItemText,
  DropdownRoot,
} from './styles';

/**
 * 상위품의 바로가기 레이어 — 외부 클릭/ESC 닫힘(anchor 클릭은 토글이라 무시), 항목은 새 창.
 */

export type ParentApprovalItem = {
  docId: string;
  subject: string;
};

type ParentApprovalDropdownProps = {
  open: boolean;
  onClose: () => void;
  parents: ParentApprovalItem[];
  /** chip wrapper ref — 외부 클릭 판정 시 anchor 영역(=chip+dropdown)은 제외 */
  anchorRef?: React.RefObject<HTMLElement | null>;
};

const buildViewerHref = (docId: string) => `/approval/viewer?doc=${docId}`;

const ParentApprovalDropdown = ({
  open,
  onClose,
  parents,
  anchorRef,
}: ParentApprovalDropdownProps) => {
  const rootRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open || parents.length === 0) return null;

  return (
    <DropdownRoot ref={rootRef} role="menu" data-print-exclude="">
      {parents.map((p) => (
        <DropdownItem key={p.docId} role="none">
          <DropdownItemLink
            href={buildViewerHref(p.docId)}
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            onClick={onClose}
          >
            <DropdownItemText>{p.subject}</DropdownItemText>
            <DropdownItemIconWrap>
              <NewWindowIcon />
            </DropdownItemIconWrap>
          </DropdownItemLink>
        </DropdownItem>
      ))}
    </DropdownRoot>
  );
};

export default ParentApprovalDropdown;
